import { Ref, forwardRef, useImperativeHandle } from "react";
import { FieldArrayWithId, useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Box, Stack } from "@mui/material";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { PollingFrequencyForm, PollingFrequencyFormObject } from "../PollingFrequencyUtils";
import PollingFrequencyObjectListUtils from "./PollingFrequencyObjectListUtils";
import VirtualizedList from "../../../components/virtualized-list/VirtualizedList";
import NoData from "../../../components/no-data/NoData";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { PollingFrequencyObjectListItem } from "./PollingFrequencyObjectListItem";
import { BaseActionEnum } from "../../../components/action-menu/ActionMenuItem";
import Enumerable from "linq";
import { sleep } from "../../../common/utilities/Sleep";
import _ from "lodash";
import { contains } from "../../../common/utilities/Contains";


export type PollingFrequencyOptionListProps = {
    objectType: eLynxObjectTypeEnum;
    visible?: boolean;
    viewSelected: boolean;
    filterText: string;
};

export type PollingFrequencyOptionListType = {
    executeAction: (action: BaseActionEnum) => void;
};

function PollingFrequencyOptionList({
    visible,
    objectType,
    viewSelected,
    filterText
}: PollingFrequencyOptionListProps, ref: Ref<unknown> | undefined) {

    const {t} = useTranslation();

    useImperativeHandle<unknown, PollingFrequencyOptionListType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    const {
        watch,
        control,
        trigger,
        getValues,
        setValue
    } = useFormContext<PollingFrequencyForm>();    

    const name = objectType === eLynxObjectTypeEnum.Device ?
                'devices' : 
                objectType === eLynxObjectTypeEnum.Tank ? 
                'tanks' :
                'tats'

    const _fieldKey = (ndx: number, selected: boolean) => `${name}.${ndx}.${selected}`;
    const {
        fields,
        update,
        replace
    } = useFieldArray({
        control: control,
        name: name,
        keyName: "fieldKey"
    });
    
    const {
        replace: replaceTats
    } = useFieldArray({
        control: control,
        name: 'tats',
        keyName: "fieldKey"
    });        

    const [
        watchItems,
        devices,
        tanks, 
    ] = watch([
        name,
        'devices',
        'tanks',
    ]);

    const selectedObjects: PollingFrequencyFormObject[] = objectType === eLynxObjectTypeEnum.Tag ? 
        devices.filter(o => o.selected).concat(tanks.filter(o => o.selected)) : 
        [];

    const tatsInUse = selectedObjects.length > 0 ? 
        Enumerable.from(selectedObjects).selectMany(o => o.tatsInUse).distinct(o => o).toArray() : 
        Enumerable.from(devices.concat(tanks)).selectMany(o => o.tatsInUse).distinct(o => o).toArray();

    const listItems = objectType === eLynxObjectTypeEnum.Tag ?
    fields.map((field, index) => {
        return {
            ...field,
            ...watchItems[index],
            fieldKey: _fieldKey(index, field.selected)
        };
    }).filter(o => tatsInUse.find(v => v === o.id) !== undefined) :
    fields.map((field, index) => {
        return {
            ...field,
            ...watchItems[index],
            fieldKey: _fieldKey(index, field.selected)
        };
    });

    const count = PollingFrequencyObjectListUtils.filterItems(listItems, filterText, viewSelected).length;

    function updateCheckState(checked: boolean) {

        const filteredItems = PollingFrequencyObjectListUtils.filterItems(listItems, filterText, viewSelected);
        
        filteredItems.forEach(o => {
            const item = listItems.find(v => v.fieldKey === o.fieldKey);

            if (item) {
                item.selected = checked;
            }

        });

        replace(_.cloneDeep(listItems));
        trigger(name);
    }

    async function updateTatList() {

        // delay setting because unchecking an objects changes the list of
        // visible tats, which will affect the indecies
        await sleep(500);
        
        const objects = devices.concat(tanks).filter(o => o.selected);

        // if there are still objects selected clear the tats that no longer are visible in the list
        // so that the dirty flags will not be affected.
        if (objects.length > 0) {

            const objectTats = objects.filter(o => o.selected).flatMap(o => o.tatsInUse);

            const tats = _.cloneDeep(getValues('tats'));
            tats.filter(o => o.selected)
            .forEach((o) => {
               
                if (!objectTats.find(v => v === o.id)) {

                    o.selected = false;

                }
            });

            replaceTats(tats);
        }
    }

    function executeAction(action: BaseActionEnum) {

        switch(action) {
            case BaseActionEnum.SelectAll:
                updateCheckState(true);
                updateTatList();
                break;

            case BaseActionEnum.RemoveAll:
                updateCheckState(false);
                updateTatList();
            
                break;

        }
    }

    function onObjectSelected(item: PollingFrequencyFormObject, value: boolean ) {

        const ndx = listItems.findIndex(v => v.fieldKey === item.fieldKey);

        update(ndx, {...item,
            selected: value
        });

        if (item.objectType === eLynxObjectTypeEnum.Device ||
            item.objectType === eLynxObjectTypeEnum.Tank) {

                setValue('newSchedule', -1, {shouldTouch: true});

                updateTatList();
        }
    }

    function renderItem(_index: number, item: PollingFrequencyFormObject) {

        return (
            <PollingFrequencyObjectListItem
                item={item}
                key={item.fieldKey}
                onSelected={onObjectSelected}
            />
        );
    };

    function onFilterItem(item: FieldArrayWithId<PollingFrequencyForm, 'devices' | 'tanks' | 'tats', 'fieldKey'>): boolean {

        let show = !!contains(item.label, filterText)

        if (show && viewSelected) {
            show = item.selected;
        }

        return show;
    }

    return (
        <Stack
            direction='row'
            sx={{
                display: 'flex',
                flex: '1 1 auto'
            }}>
            <Box
                sx={{
                    display: 'flex',
                    flex: '1 1 auto',
                    flexDirection: 'column',
                    overflowY: 'auto'
                }}>
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    overflow: "hidden"
                    }}>

                    {count === 0 &&         
                        <Box sx={{
                            padding: 1
                        }}>
                            <NoData text={t('pollingFrequencyAdmin.nodata.noFilterMatch', {name: getObjectTypeName(objectType, true, true)})} />
                        </Box>
                    }
                                            
                    <VirtualizedList
                        items={listItems}
                        renderItem={renderItem}
                        filter={onFilterItem}
                    />

                </Box>
            </Box>
        </Stack>
    );
}

export default forwardRef(PollingFrequencyOptionList);